/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import AgePrompt, {
  AGE_VERIFICATION_STATUS,
} from 'bundles/enterprise-legacy-learner-home/components/age-verification/AgePrompt';
import type { ageVerificationStatusValues } from 'bundles/enterprise-legacy-learner-home/components/age-verification/AgePrompt';
import AgeVerificationFail from 'bundles/enterprise-legacy-learner-home/components/age-verification/AgeVerificationFail';
import AgeVerificationSuccess from 'bundles/enterprise-legacy-learner-home/components/age-verification/AgeVerificationSuccess';
import { getAgeVerification } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeUtils';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';

type Props = {
  programId: string;
  program: EnterpriseProgramsV1;
  isAuthenticatedUser: boolean;
  setEnableAgeVerification: (val: boolean) => void;
};

const styles = {
  page: css`
    background-color: var(--cds-color-neutral-disabled-weak);
    min-height: 100vh;
  `,
  container: css`
    margin-left: auto;
    margin-right: auto;
    max-width: ${breakpoints.values.md}px;
    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-200);
    }
  `,
  main: css`
    background-color: var(--cds-color-neutral-background-primary);
    padding: 0 var(--cds-spacing-200);

    &::before,
    &::after {
      content: '';
      display: table;
    }

    &::before {
      margin-bottom: var(--cds-spacing-800);
    }

    &::after {
      margin-top: var(--cds-spacing-800);
    }
    ${breakpoints.down('xs')} {
      &::before {
        margin-bottom: var(--cds-spacing-300);
      }

      &::after {
        margin-top: var(--cds-spacing-300);
      }
    }
  `,
};

function AgeVerificationPage({
  programId,
  program,
  isAuthenticatedUser,
  setEnableAgeVerification,
}: Props): JSX.Element {
  const [ageVerificationState, setAgeVerificationState] = React.useState<ageVerificationStatusValues>();

  React.useEffect(() => {
    const ageVerification = getAgeVerification(programId, isAuthenticatedUser);
    if (ageVerification === undefined) {
      setAgeVerificationState(AGE_VERIFICATION_STATUS.NOT_ANSWERED);
    } else if (ageVerification === false) {
      setAgeVerificationState(AGE_VERIFICATION_STATUS.FAIL);
    } else {
      setAgeVerificationState(AGE_VERIFICATION_STATUS.SUCCESS);
    }
  }, [programId, isAuthenticatedUser]);

  return (
    <div css={styles.container}>
      <main css={styles.main}>
        {ageVerificationState === AGE_VERIFICATION_STATUS.NOT_ANSWERED && (
          <AgePrompt
            programId={programId}
            isAuthenticatedUser={isAuthenticatedUser}
            onContinueClick={setAgeVerificationState}
          />
        )}
        {ageVerificationState === AGE_VERIFICATION_STATUS.FAIL && <AgeVerificationFail programName={program.name} />}
        {ageVerificationState === AGE_VERIFICATION_STATUS.SUCCESS && (
          <AgeVerificationSuccess programName={program.name} setEnableAgeVerification={setEnableAgeVerification} />
        )}
      </main>
    </div>
  );
}

export default AgeVerificationPage;
